import type { ActorDTO, DecisionDTO, EventDTO, TaskDTO } from "@roomate/core";
import { Pill } from "./calm.tsx";
import { inputCls } from "./ui.tsx";

export type AbandonmentView = {
  actorId: string | null;
  actorName: string;
  at: number;
  reason: string;
};

/** Mirrors the endpoint's person/write-access/state gate so forbidden actions are never offered. */
export function canAbandonMerge(actor: Pick<ActorDTO, "kind" | "access">, task: Pick<TaskDTO, "stage" | "branch" | "mergeState" | "labels">): boolean {
  // merge-resolve chores never enter the merge queue, so the server refuses to abandon them (409).
  if (task.labels?.includes("merge-resolve")) return false;
  return actor.kind === "human" && actor.access !== "viewer" && task.stage === "done" && !!task.branch && (task.mergeState === null || task.mergeState === "merge_failed");
}

/** The latest event wins, matching the server's append-only audit log. */
export function abandonmentFor(task: Pick<TaskDTO, "id" | "mergeState">, events: readonly EventDTO[], actors: readonly ActorDTO[] = []): AbandonmentView | null {
  if (task.mergeState !== "abandoned") return null;
  const event = events.findLast((candidate) => candidate.type === "task.abandoned" && candidate.subjectId === task.id);
  if (!event) return null;
  const actorId = typeof event.payload.actorId === "string" ? event.payload.actorId : event.actorId;
  return {
    actorId,
    actorName: actors.find((actor) => actor.id === actorId)?.name ?? "Unknown person",
    at: event.ts,
    reason: typeof event.payload.reason === "string" ? event.payload.reason : "No reason recorded",
  };
}

export type AffectedDependent = { task: TaskDTO; depth: number };

/** Mirrors the server's reverse graph walk: traverse through terminal tasks, but list only live dependents. */
export function dependentsForAbandon(task: Pick<TaskDTO, "id">, tasks: readonly TaskDTO[]): AffectedDependent[] {
  const reverse = new Map<string, TaskDTO[]>();
  for (const candidate of tasks) {
    for (const dependencyId of candidate.dependsOn) reverse.set(dependencyId, [...(reverse.get(dependencyId) ?? []), candidate]);
  }
  const queue = (reverse.get(task.id) ?? []).map((dependent) => ({ task: dependent, depth: 1 }));
  const seen = new Set<string>();
  const affected: AffectedDependent[] = [];
  while (queue.length) {
    const current = queue.shift()!;
    if (seen.has(current.task.id)) continue;
    seen.add(current.task.id);
    for (const dependent of reverse.get(current.task.id) ?? []) queue.push({ task: dependent, depth: current.depth + 1 });
    // A done dependent still waiting to merge (branch, no merge result) is blocked too, as on the server.
    const waitingToMerge = current.task.stage === "done" && !!current.task.branch && current.task.mergeState == null;
    if ((current.task.stage !== "done" && current.task.stage !== "cancelled") || waitingToMerge) affected.push(current);
  }
  return affected;
}

export function abandonedDependency(task: Pick<TaskDTO, "dependsOn">, tasks: readonly TaskDTO[]): TaskDTO | null {
  return task.dependsOn.map((id) => tasks.find((candidate) => candidate.id === id)).find((candidate) => candidate?.mergeState === "abandoned") ?? null;
}

export function dependencyLabel(task: Pick<TaskDTO, "dependsOn">, tasks: readonly TaskDTO[]): string | null {
  const abandoned = abandonedDependency(task, tasks);
  if (abandoned) return `Blocked: depends on abandoned #${abandoned.number}`;
  const dependencies = task.dependsOn.map((id) => tasks.find((candidate) => candidate.id === id)).filter((candidate): candidate is TaskDTO => !!candidate);
  return dependencies.length ? `Depends on ${dependencies.map((dependency) => `#${dependency.number}`).join(", ")}` : null;
}

export function AbandonedBadge({ abandonment }: { abandonment?: AbandonmentView | null }) {
  return (
    <span title={abandonment?.reason} data-testid="abandoned-badge">
      <Pill tone="signal">Abandoned</Pill>
    </span>
  );
}

export function AbandonmentLine({ abandonment }: { abandonment: AbandonmentView }) {
  const date = new Date(abandonment.at).toLocaleDateString([], { dateStyle: "medium" });
  return (
    <p data-testid="abandonment-line" className="text-[14px] leading-relaxed">
      <span className="font-medium">Abandoned by {abandonment.actorName} on {date}:</span> {abandonment.reason}
    </p>
  );
}

export function AbandonBlastRadius({ dependents, decisions }: { dependents: readonly AffectedDependent[]; decisions: readonly DecisionDTO[] }) {
  return (
    <div className="space-y-4" data-testid="abandon-blast-radius">
      <section>
        <h3 className="text-[13px] font-medium text-ink-2">Dependents that will remain blocked</h3>
        {dependents.length ? (
          <ul className="mt-1.5 list-disc space-y-1 pl-5 text-[13px] text-ink-2">
            {dependents.map(({ task, depth }) => <li key={task.id}>#{task.number} {task.title}{depth > 1 ? " (transitive)" : ""}</li>)}
          </ul>
        ) : <p className="mt-1.5 text-[13px] text-ink-3">No open dependents.</p>}
      </section>
      <section>
        <h3 className="text-[13px] font-medium text-ink-2">Open decisions that will be cancelled</h3>
        {decisions.length ? (
          <ul className="mt-1.5 list-disc space-y-1 pl-5 text-[13px] text-ink-2">
            {decisions.map((decision) => <li key={decision.id}>{decision.question}</li>)}
          </ul>
        ) : <p className="mt-1.5 text-[13px] text-ink-3">No open decisions.</p>}
      </section>
    </div>
  );
}

export function AbandonReasonInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <textarea
      className={inputCls}
      rows={3}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      minLength={10}
      required
      autoFocus
      aria-describedby="abandon-reason-hint"
      data-testid="abandon-reason"
    />
  );
}
